/**
 * 洪水与城市内涝 — 公开资料研究参考的内容层（SC-FLOOD-01）。
 *
 * 内容来自用户提供的 SC-FLOOD-01 执行包：
 *   02_CONTENT/scenario.json（6 个典型问题、30 项信息需求、9 类能力研究主题）
 *   03_SOURCES/detailed-evidence.csv（18 条章节级依据映射）
 *   03_SOURCES/sources.csv（8 个政府权威来源）
 *   04_IMAGES（5 张 AI 生成示意图）
 *
 * 这一层不是：
 *   不是正式 Capability 定义或 Capability ID；不是设备清单或配备状态；
 *   不是属地防汛预案、实时水情、现场事实或行动授权；不创建 Mission 或数据库记录。
 *
 * 安全边界：
 *   不生成实时水位、洪水预报、风险数值、警戒或疏散距离；
 *   不自动控制泵站、闸门，不自动指派救援；
 *   不把郑州“7·20”同一份调查报告中的相关子事件拆成多份独立官方调查报告。
 */

export const floodReferenceNature = 'PUBLIC_RESEARCH_REFERENCE_PENDING_SCENARIO_OWNER';

export const floodSafetyNotice =
  '本内容为公开资料研究参考，不是地方政府正式作战预案，不代表实时水情、洪水预报、警戒或疏散距离，也不构成泵站、闸门控制或救援任务授权。实际处置应由属地防汛抗旱指挥体系与专业部门依据现场证据、适用法规和专业人员判断组织实施。';

export const floodMediaDisclaimer =
  '以下画面为AI生成示意图，非真实灾害现场照片，不代表实际监测数据。';

/* ------------------------------------------------------------------ 来源 */

export interface FloodSource {
  readonly id: string;
  readonly title: string;
  readonly org: string;
  readonly kind: string;
  readonly code: string;
  readonly published: string;
  /** 版本与取得状态：现行、待复核或未取得正文。必须与内容分开显示。 */
  readonly status: string;
  /** 适用边界。 */
  readonly scope: string;
  readonly url: string;
  readonly documentUrl?: string;
  readonly documentLabel?: string;
}

export const floodSources: readonly FloodSource[] = [
  {
    id: 'S01',
    title: '《国务院办公厅关于加强城市内涝治理的实施意见》',
    org: '国务院办公厅',
    kind: '国务院政策文件',
    code: '国办发〔2021〕11号',
    published: '2021',
    status: '现行政策文件；以官方公开页面为准，本轮未逐条核对后续修订',
    scope: '城市内涝治理的总体要求、设施建设与应急管理；不能证明任何具体城市已达到某种排水能力。',
    url: 'https://www.beijing.gov.cn/zhengce/zhengcefagui/202104/t20210426_2373347.html',
  },
  {
    id: 'S02',
    title: '《关于进一步规范城市内涝防治信息发布等有关工作的通知》',
    org: '住房和城乡建设部等三部门',
    kind: '部门通知',
    code: '建办城〔2022〕30号',
    published: '2022',
    status: '现行通知；以官方公开页面为准',
    scope:
      '气象预警预报信息表述、雨水管渠设计标准与防灾避险提醒的规范要求；不能用设计标准推导实时安全状态。',
    url: 'https://app.www.gov.cn/govdata/gov/202207/17/487414/article.html',
  },
  {
    id: 'S03',
    title: '《关于做好2024年城市排水防涝工作的通知》',
    org: '住房城乡建设部办公厅',
    kind: '年度工作通知',
    code: '建办城函〔2024〕106号',
    published: '2024',
    status: '2024 年度工作通知；不代表当前所有设施的实时状态',
    scope: '城市排水防涝设施、重点部位与应急处置的年度工作要求；年度安排不能替代实时检测。',
    url: 'https://www.hunan.gov.cn/zqt/zcsd/202404/t20240402_33267946.html',
  },
  {
    id: 'S04',
    title: '《中华人民共和国防洪法》',
    org: '全国人民代表大会常务委员会',
    kind: '法律',
    code: '2016 年修订',
    published: '2016',
    status: '2016 年修订版本；以国家法律法规数据库页面为准',
    scope: '防洪规划、防洪工程、洪水预报与防汛抗洪职责；不能自动生成地方防洪调度指令。',
    url: 'https://flk.npc.gov.cn/detail?id=2c909fdd678bf17901678bf8122508f7',
  },
  {
    id: 'S05',
    title: '《山洪灾害防御预案编制技术导则》',
    org: '水利部',
    kind: '行业标准信息',
    code: 'SL/T 666—2024',
    published: '2024 发布／2025 实施',
    status: '标准信息页；本轮未取得标准正文，不推断具体条款',
    scope:
      '山洪灾害防御预案编制的适用范围提示；不能和城市雨水排水混为一类，也不能当作城市内涝统一规程。',
    url: 'https://hbba.sacinfo.org.cn/stdDetail/63c3452bbec96c9783d8922e913f1a604a71658ab56fa22d356104c3561fd11b',
  },
  {
    id: 'S06',
    title: '河南郑州“7·20”特大暴雨灾害调查报告',
    org: '国务院灾害调查组',
    kind: '官方灾害调查报告',
    code: '官方调查报告',
    published: '2022',
    status: '官方报告下载端点；本页只提供官方链接，未随包收录原件，未逐页核对页码',
    scope:
      '报告中的监测预报、应急处置、交通运输、城市内涝及相关子事件章节；历史灾害不能当作通用现场处置流程。',
    url: 'https://www.mem.gov.cn/xw/bndt/202201/t20220121_407106.shtml',
    documentUrl: 'https://xxgk.mem.gov.cn/ezweb/ctrl/news/download?fileId=1825175337991610',
    documentLabel: '官方调查报告下载（链接直达官方，未随包收录原件）',
  },
  {
    id: 'S07',
    title: '国务院调查组关于郑州“7·20”灾害答记者问',
    org: '应急管理部',
    kind: '官方调查解读',
    code: '官方发布',
    published: '2022',
    status: '官方网页公开可读',
    scope:
      '就地铁 5 号线、京广快速路北隧道等事件答记者问；不得由该次责任认定推断其他运营单位责任。',
    url: 'https://www.mem.gov.cn/xw/bndt/202201/t20220121_407135.shtml',
  },
  {
    id: 'S08',
    title: '《河南郑州“7·20”特大暴雨灾害调查报告》公布信息',
    org: '应急管理部',
    kind: '官方公告',
    code: '官方公告',
    published: '2022',
    status: '官方网页公开可读',
    scope: '调查组背景、调查分工和主要公开说明；不替代完整报告逐章查阅。',
    url: 'https://www.mem.gov.cn/xw/bndt/202201/t20220121_407106.shtml',
  },
] as const;

export function findFloodSource(id: string): FloodSource | undefined {
  return floodSources.find((row) => row.id === id);
}

/* ------------------------------------------------------------------ 依据卡 */

export type FloodEvidenceCategory = '法律法规' | '政策文件' | '标准信息' | '灾害调查报告';

export const floodEvidenceCategoryOrder: readonly FloodEvidenceCategory[] = [
  '法律法规',
  '政策文件',
  '标准信息',
  '灾害调查报告',
];

export interface FloodEvidence {
  readonly id: string;
  readonly sourceId: string;
  /** 官方文件中的章节、条款标题或报告章节；没有核验到条款号时不猜编号。 */
  readonly locator: string;
  readonly label: string;
  /** 这条依据说明什么（业务语言）。 */
  readonly plain: string;
  /** 与当前问题的关系。 */
  readonly appliesTo: string;
  /** 不能从中推导什么。 */
  readonly cannot: string;
  readonly issueIds: readonly string[];
  readonly category: FloodEvidenceCategory;
  /** 确有对应事件时才填写；没有关系就留空。 */
  readonly caseIds: readonly string[];
}

export const floodEvidence: readonly FloodEvidence[] = [
  {
    id: 'E01',
    sourceId: 'S02',
    locator: '第一部分：规范气象预警预报信息表述',
    label: '降雨预警要带时间尺度、单位和发布口径',
    plain: '不同时间尺度的降水量要带单位和时间范围，不能直接混写。',
    appliesTo: '为雨情监测卡保留“发布者、时间尺度、单位和时间戳”。',
    cannot: '未核实某次降雨等级或积水安全性。',
    issueIds: ['FL-01'],
    category: '政策文件',
    caseIds: [],
  },
  {
    id: 'E02',
    sourceId: 'S01',
    locator: '总体要求、全面治理与突出重点',
    label: '城市内涝不只看瞬时降雨',
    plain: '城市内涝不仅取决于瞬时降雨，还涉及排水、空间和应急管理。',
    appliesTo: '需要积水位置与城市排水资料相互关联。',
    cannot: '不证明本地排水能力已达到标准。',
    issueIds: ['FL-01', 'FL-04'],
    category: '政策文件',
    caseIds: [],
  },
  {
    id: 'E03',
    sourceId: 'S03',
    locator: '城市排水防涝重点部位与应急工作',
    label: '排水设施和重点部位需要日常掌握',
    plain: '排水设施和重点部位需要日常巡查与信息掌握。',
    appliesTo: '适合建立易涝点、道路和设施状态字段。',
    cannot: '年度工作安排不能替代实时检测。',
    issueIds: ['FL-01', 'FL-04', 'FL-06'],
    category: '政策文件',
    caseIds: [],
  },
  {
    id: 'E04',
    sourceId: 'S04',
    locator: '防洪规划和洪水防御相关规定',
    label: '河道洪水与城市积水是不同管理对象',
    plain: '河道洪水、水工程和城市积水涉及不同管理对象。',
    appliesTo: '河道水位、堤岸、上游来水与城市低洼区分别展示。',
    cannot: '不构成水库或闸门调度命令。',
    issueIds: ['FL-02'],
    category: '法律法规',
    caseIds: [],
  },
  {
    id: 'E05',
    sourceId: 'S01',
    locator: '防御外洪与治理内涝并重',
    label: '外洪与内涝可能相互影响',
    plain: '城市内涝与外部洪水可能相互影响。',
    appliesTo: '河道高水位与排水出口的背景信息需要一并观察。',
    cannot: '不是已确定的外水顶托结论。',
    issueIds: ['FL-02'],
    category: '政策文件',
    caseIds: [],
  },
  {
    id: 'E06',
    sourceId: 'S05',
    locator: '山洪灾害防御预案编制技术导则（适用范围核查）',
    label: '山洪资料不能等同于城市内涝规程',
    plain: '山洪是不同于一般城市积水的场景分支。',
    appliesTo: '将相关河谷或山地风险标注为扩展参考。',
    cannot: '不把山洪预案当作城市内涝统一SOP。',
    issueIds: ['FL-02'],
    category: '标准信息',
    caseIds: [],
  },
  {
    id: 'E07',
    sourceId: 'S07',
    locator: '地铁5号线倒灌事件问答段落',
    label: '地铁倒灌要看入口和围护路径',
    plain: '官方调查认定城市内涝通过停车场围墙等路径灌入地铁隧道。',
    appliesTo: '地下空间应关注入口、围护设施、内部水情与运营状态。',
    cannot: '不代表所有地下空间有同样的进水机理。',
    issueIds: ['FL-03'],
    category: '灾害调查报告',
    caseIds: ['C01'],
  },
  {
    id: 'E08',
    sourceId: 'S06',
    locator: '调查报告交通运输、城市内涝相关章节',
    label: '地下交通受灾要同时看设施、运营与人员',
    plain: '地下交通受灾要同时关注设施、运营和人员风险。',
    appliesTo: '把地铁、隧道、地下车库区分管理。',
    cannot: '不从历史事故推断实时人员位置。',
    issueIds: ['FL-03', 'FL-05', 'FL-06'],
    category: '灾害调查报告',
    caseIds: ['C01', 'C02'],
  },
  {
    id: 'E09',
    sourceId: 'S06',
    locator: '调查报告监测预报与城市内涝相关章节',
    label: '雨情与受影响范围要多部门核对',
    plain: '雨情观测与受影响范围需要经由多个部门信息核对。',
    appliesTo: '为线索添加时间、来源和核实状态。',
    cannot: '不能用灾后报告代替现时预报。',
    issueIds: ['FL-01', 'FL-05'],
    category: '灾害调查报告',
    caseIds: [],
  },
  {
    id: 'E10',
    sourceId: 'S07',
    locator: '地铁5号线、京广北隧道事件答记者问',
    label: '受困人员与封闭疏导信息相关',
    plain: '事故显示受困人员与封闭、疏导措施信息相关。',
    appliesTo: '明确区分已确认受困、失联和已转移。',
    cannot: '不直接产生救援行动或风险距离。',
    issueIds: ['FL-03', 'FL-05', 'FL-06'],
    category: '灾害调查报告',
    caseIds: ['C01', 'C02'],
  },
  {
    id: 'E11',
    sourceId: 'S01',
    locator: '城市排水防涝设施与应急管理内容',
    label: '地下空间设施应在防涝管理中核查',
    plain: '地下空间的设施应在防涝管理背景中核查。',
    appliesTo: '建立地下入口、排水、供电与防水设施清单。',
    cannot: '不意味着这些设施现已可用。',
    issueIds: ['FL-03'],
    category: '政策文件',
    caseIds: [],
  },
  {
    id: 'E12',
    sourceId: 'S03',
    locator: '城市排水防涝重点部位及设备检查',
    label: '泵站、雨水口与易涝点资料应可查询',
    plain: '排水泵站、雨水口和重点易涝点信息应可查询。',
    appliesTo: '设施图层与管理信息分离于实时状态。',
    cannot: '不能从设备名判断实际运行正常。',
    issueIds: ['FL-04'],
    category: '政策文件',
    caseIds: [],
  },
  {
    id: 'E13',
    sourceId: 'S07',
    locator: '京广快速路北隧道事件问答段落',
    label: '隧道封闭与交通疏导具有时效性',
    plain: '封闭隧道与交通疏导时效对风险有影响。',
    appliesTo: '将官方封闭、疏导通报与普通群众线索区分。',
    cannot: '不能代替交管部门发布实时管制指令。',
    issueIds: ['FL-06'],
    category: '灾害调查报告',
    caseIds: ['C02'],
  },
  {
    id: 'E14',
    sourceId: 'S02',
    locator: '城市排水设计标准表述与暴雨强度公式',
    label: '设计标准与降雨指标有具体口径',
    plain: '设计标准与降雨指标有具体单位口径。',
    appliesTo: '排水设计资料与现场积水信息分开展示。',
    cannot: '设计标准不是实时容量或安全阈值。',
    issueIds: ['FL-04'],
    category: '政策文件',
    caseIds: [],
  },
  {
    id: 'E15',
    sourceId: 'S06',
    locator: '灾害应对处置和相关人员事件章节',
    label: '人员线索要记录人数、位置、时间与来源',
    plain: '救援需要人员、交通和多部门信息。',
    appliesTo: '人数、位置、时间、来源应成为人员线索字段。',
    cannot: '不可由相似案例推断个人生存状态。',
    issueIds: ['FL-05'],
    category: '灾害调查报告',
    caseIds: ['C01', 'C02'],
  },
  {
    id: 'E16',
    sourceId: 'S01',
    locator: '健全工作制度与部门协调联动内容',
    label: '防洪排涝需要部门间协调',
    plain: '防洪排涝与应急管理需要部门间协调。',
    appliesTo: '平台提供信息共享线索而不改变指挥权。',
    cannot: '不构成跨部门授权，也不代表平台可以调取其他部门的实时数据。',
    issueIds: ['FL-06'],
    category: '政策文件',
    caseIds: [],
  },
  {
    id: 'E17',
    sourceId: 'S06',
    locator: '调查报告相关地方和部门责任问题章节',
    label: '通报与处置时间线需要可追溯',
    plain: '通报信息和处置时间线需要可追溯。',
    appliesTo: '保留来源、报告时间、更新状态。',
    cannot: '不由平台作行政责任认定。',
    issueIds: ['FL-06'],
    category: '灾害调查报告',
    caseIds: ['C01', 'C02'],
  },
  {
    id: 'E18',
    sourceId: 'S06',
    locator: '郭家咀水库漫坝事件相关章节',
    label: '上游水工程与下游影响需要分开记录',
    plain: '上游水工程和下游受影响对象可能存在复杂关系。',
    appliesTo: '为河道/水库资料保留属地水利部门来源。',
    cannot: '不能从历史漫坝事件给出具体调度建议。',
    issueIds: ['FL-02'],
    category: '灾害调查报告',
    caseIds: ['C03'],
  },
] as const;

export function findFloodEvidence(id: string): FloodEvidence | undefined {
  return floodEvidence.find((row) => row.id === id);
}

/**
 * 逐问题｜依据的关系说明。
 *
 * 同一份文件在不同问题中支撑的信息不同，这里按“问题｜依据”保留一句业务解释，
 * 避免把来源的通用摘要直接当作当前问题的结论。
 */
const relations: Readonly<Record<string, string>> = {
  'FL-01|E01': '预警信息必须带发布机构、时间尺度和单位，本问题第一条信息需求就按这四项记录。',
  'FL-01|E02': '内涝由降雨、排水、空间和应急多环节共同决定，所以积水位置要与排水资料一起核对。',
  'FL-01|E03': '重点部位与设施需要日常掌握，本问题因此要列出周边低洼点、道路和重要设施。',
  'FL-01|E09': '灾后报告显示雨情与受影响范围要跨部门核对，本问题因此保留来源与核实状态字段。',
  'FL-02|E04':
    '法律把河道洪水、水工程和城市积水作为不同管理对象，本问题分别记录水位、堤岸和排水出口。',
  'FL-02|E05': '外洪与内涝可能叠加，本问题要求把河道高水位与排水出口放在同一观察背景中。',
  'FL-02|E06': '山洪属于扩展参考分支，本问题提示不要把山洪预案直接当作城市内涝规程。',
  'FL-02|E18': '郭家咀水库事件说明上游水工程与下游对象关系复杂，本问题为水库资料保留属地来源。',
  'FL-03|E07': '地铁 5 号线事件表明进水有具体路径，本问题先要掌握入口、围护设施和内部水情。',
  'FL-03|E08': '报告把地下交通的设施、运营和人员问题放在一起，本问题因此区分地铁、隧道和地下车库。',
  'FL-03|E10':
    '答记者问把受困人员与封闭、疏导措施联系起来，本问题要求区分已确认受困、失联和已转移。',
  'FL-03|E11': '地下空间设施要在防涝管理中核查，本问题列出排水、挡水、供电与通信设施。',
  'FL-04|E02': '内涝治理需要把积水位置与排水资料关联，本问题先掌握雨水口和低洼区情况。',
  'FL-04|E03': '重点部位需要日常巡查与信息掌握，本问题记录易涝点历史与实时视频。',
  'FL-04|E12': '泵站、雨水口和易涝点资料应可查询，本问题把设施台账与运行告警分开记录。',
  'FL-04|E14': '设计标准与降雨指标有具体口径，本问题把设计资料与现场积水信息分开展示。',
  'FL-05|E08': '地下交通受灾要同时看设施、运营和人员，本问题记录水流、障碍物、道路与天气观测。',
  'FL-05|E09': '雨情与受影响范围要跨部门核对，本问题为人员线索保留来源、时间与核实状态。',
  'FL-05|E10': '郑州事件表明受困人员与封闭疏导信息相关，本问题区分已确认受困、失联和已转移。',
  'FL-05|E15': '人员救援需要人数、位置、时间和来源，本问题把这些作为人员线索的基本字段。',
  'FL-06|E03': '重点部位与道路信息需要日常掌握，本问题据此整理替代通道与受影响设施。',
  'FL-06|E08': '地下交通受灾要同时看设施、运营和人员，本问题把道路、隧道和运营状态分开记录。',
  'FL-06|E10': '郑州事件显示封闭与疏导信息影响人员风险，本问题要求区分官方通报与群众线索。',
  'FL-06|E13': '京广快速路北隧道事件说明封闭与疏导具有时效性，本问题记录管制生效时间与发布来源。',
  'FL-06|E16': '防洪排涝需要部门协调，本问题整理交通、水务、消防、医疗的报告而不改变指挥权。',
  'FL-06|E17': '通报和处置时间线需要可追溯，本问题保留来源、报告时间和更新状态。',
};

export function floodEvidenceRelation(issueId: string, evidenceId: string): string {
  return (
    relations[issueId + '|' + evidenceId] ??
    findFloodEvidence(evidenceId)?.appliesTo ??
    '本场景未登记这份资料与该问题之间的逐项关系。'
  );
}

/* ------------------------------------------------------------------ 案例 */

export interface FloodCase {
  readonly id: string;
  readonly title: string;
  readonly kind: string;
  readonly date: string;
  readonly location: string;
  readonly sourceIds: readonly string[];
  readonly section: string;
  readonly factual: string;
  readonly relevance: string;
  readonly limit: string;
  /** 适用范围标签，防止把某一事件直接推导为其他地区结论。 */
  readonly scopeLabel: string;
  readonly scopeNote: string;
}

/** 三个事件同属一份调查报告；页面在任何案例列表前固定显示这句。 */
export const floodCaseNotice =
  '以下事件均出自同一份《河南郑州“7·20”特大暴雨灾害调查报告》及其官方答记者问，是同一调查中的相关子事件，不是多份独立官方调查报告。';

export const floodCases: readonly FloodCase[] = [
  {
    id: 'C01',
    title: '郑州地铁 5 号线“7·20”亡人事件',
    kind: '官方灾害调查报告中的城市轨道交通事件',
    date: '2021-07-20',
    location: '河南郑州',
    sourceIds: ['S06', 'S07', 'S08'],
    section:
      '《河南郑州“7·20”特大暴雨灾害调查报告》“郑州地铁 5 号线亡人事件”专题；应急管理部调查组答记者问相关问答',
    factual:
      '调查报告以专题记录极端暴雨期间地铁隧道进水及人员伤亡情况，并对预警、运营调度、结构防护和应急处置等问题作出认定；答记者问说明了内涝水通过停车场围墙等路径灌入隧道的调查认定。',
    relevance: '地下空间倒灌、入口与围护设施、内部水情、人员状态与运营信息协同。',
    limit: '这是特定城市特定灾害的调查结论，不能直接用其水深、降雨或时序值推导其他站点的运营阈值。',
    scopeLabel: '地铁区间 · 地下空间',
    scopeNote: '适用范围内的地下轨道事件；与京广快速路北隧道事件分别记录，事实与责任认定不合并。',
  },
  {
    id: 'C02',
    title: '郑州京广快速路北隧道“7·20”亡人事件',
    kind: '官方灾害调查报告中的城市道路隧道事件',
    date: '2021-07-20',
    location: '河南郑州',
    sourceIds: ['S06', 'S07', 'S08'],
    section:
      '《河南郑州“7·20”特大暴雨灾害调查报告》“京广快速路北隧道亡人事件”专题；应急管理部调查组答记者问相关问答',
    factual:
      '调查报告以专题记录极端暴雨期间京广快速路北隧道积水及人员伤亡情况，调查组指出隧道封闭和交通疏导不及时是事件暴露的重要问题。',
    relevance: '道路隧道积水、通行管制状态、车辆与人员位置、官方通报与群众线索的区分。',
    limit: '不能将特定事件的责任认定直接套用于其他地下通道，也不构成实时交通管制指令。',
    scopeLabel: '城市道路隧道 · 地下空间',
    scopeNote: '与本场景的道路隧道问题直接对应；与地铁 5号线事件是同一调查报告中的两起不同事件。',
  },
  {
    id: 'C03',
    title: '郑州郭家咀水库“7·20”漫坝事件',
    kind: '官方灾害调查报告中的上游水工程事件',
    date: '2021-07-20',
    location: '河南郑州',
    sourceIds: ['S06', 'S08'],
    section: '《河南郑州“7·20”特大暴雨灾害调查报告》“郭家咀水库漫坝事件”相关章节',
    factual:
      '调查报告以相关章节记录郭家咀水库在极端暴雨过程中出现漫坝险情，并涉及上游水工程与下游受影响对象的关系。',
    relevance: '河道与水库资料、上游来水、属地水利部门来源与下游影响对象的分别记录。',
    limit: '不能从历史漫坝事件推导具体水库的调度方案，也不能给出警戒或疏散距离。',
    scopeLabel: '上游水库 · 河道洪水',
    scopeNote: '属于河道洪水与外水影响研究；不把城市道路积水与水库险情合并成同一结论。',
  },
] as const;

export function findFloodCase(id: string): FloodCase | undefined {
  return floodCases.find((row) => row.id === id);
}

/* ------------------------------------------------------------------ 能力研究主题 */

export interface FloodCapability {
  readonly id: string;
  readonly title: string;
  readonly image: string;
  /** 具体作用。 */
  readonly role: string;
  readonly inputs: readonly string[];
  /** 可提供的结果。 */
  readonly outputs: readonly string[];
  readonly limitation: string;
}

export const floodCapabilities: readonly FloodCapability[] = [
  {
    id: 'K01',
    title: '雨情与水位监测',
    image: '02-river-gauge.webp',
    role: '降雨量、水位和监测时间地点的记录，可辅助观察变化趋势。',
    inputs: ['监测点位置与设备说明', '雨量／水位记录及时间戳', '发布机构与采集方式'],
    outputs: ['带来源和时间戳的雨量/水位记录', '多站点对照'],
    limitation: '不能把单个水位点直接推断成全城或全部河段的安全状态。',
  },
  {
    id: 'K02',
    title: '多源信息汇总核验',
    image: '00-flood-cover.webp',
    role: '对照气象、水利、排水、视频和人工报告，保留时间与来源差异。',
    inputs: ['气象、水利、排水、交通和现场报告', '发布时间与来源', '待核实线索'],
    outputs: ['信息时间线', '冲突提示与待核实清单'],
    limitation: '模型标注不是已核实现场事实，也不能自动触发行动。',
  },
  {
    id: 'K03',
    title: '积水区域远程观察',
    image: '01-street-monitoring.webp',
    role: '通过监控、无人机或合规远程摄像观察路面与入口变化。',
    inputs: ['已获准的监控或无人机画面', '摄像头位置与时间标识', '观测范围说明'],
    outputs: ['可见光画面与位置线索', '变化记录'],
    limitation: '图像无法单独确认水深、电气风险、流速和人员安全。',
  },
  {
    id: 'K04',
    title: '洪水与地形信息查看',
    image: '02-river-gauge.webp',
    role: '结合河道、地势、桥涵、道路、低洼点等已有地图与水文资料。',
    inputs: ['河道、地势、桥涵、道路与低洼点地图', '水文资料', '数据版本与更新时间'],
    outputs: ['可核对的河道位置与设施分布', '地形图层'],
    limitation: '地图数据可能过期；不直接生成风险边界或疏散距离。',
  },
  {
    id: 'K05',
    title: '地下空间侦察与空间信息',
    image: '03-underground-inundation.webp',
    role: '使用已获准的固定视频、建筑资料或远程设备了解地下空间通道和积水线索。',
    inputs: [
      '地下空间图纸与出入口资料',
      '已获准的固定视频或远程设备',
      '管理单位的通行与设备适用性确认',
    ],
    outputs: ['通道示意与远程图像', '待查区域清单'],
    limitation: '设备电气/防水适用性、通行权限必须由管理单位及专业人员确认。',
  },
  {
    id: 'K06',
    title: '排水设施状态汇总',
    image: '01-street-monitoring.webp',
    role: '汇总城市泵站、管渠、雨水口及运行告警信息。',
    inputs: ['泵站、管渠、雨水口台账', '运行告警与检查记录', '设施管理单位信息'],
    outputs: ['设施位置与状态时间线', '故障待核实项'],
    limitation: '通用研究不等于已经接入实时泵站或已获远程控制权限。',
  },
  {
    id: 'K07',
    title: '人员位置与通信联络',
    image: '04-command-coordination.webp',
    role: '整合求助、救援队伍、交通管理和医疗单位经确认的人员线索。',
    inputs: ['求助与人员报告', '救援队伍、交通管理和医疗单位记录', '联系时间与交接状态'],
    outputs: ['人员位置线索与联系情况', '交接记录'],
    limitation: '不得用未确认线索认定人员已被救出或已转移。',
  },
  {
    id: 'K08',
    title: '水域救援协同与转运',
    image: '04-command-coordination.webp',
    role: '展示水域专业救援人员、船艇与医疗转运所需协同信息。',
    inputs: [
      '水域救援队伍与船艇能力记录（若真实接入）',
      '水流、障碍物与天气观测',
      '医疗、安置与转运信息',
    ],
    outputs: ['资源需求描述', '专业联络与转运交接信息'],
    limitation: '不自动推荐入水作业路线、人员下水、船艇下水条件或具体救援动作。',
  },
  {
    id: 'K09',
    title: '现场通信与人员联络',
    image: '04-command-coordination.webp',
    role: '确保不同部门和现场分队共享可追溯的信息。',
    inputs: ['对讲、广播与现场通信状态', '部门联系渠道', '任务交接与报告时间线'],
    outputs: ['联系渠道与任务交接状态', '报告时间线'],
    limitation: '不代表平台已拥有真实通信网络或指挥权限。',
  },
] as const;

export function findFloodCapability(id: string): FloodCapability | undefined {
  return floodCapabilities.find((row) => row.id === id);
}

export function floodIssuesForCapability(id: string): readonly string[] {
  return floodIssues.filter((issue) => issue.capabilityIds.includes(id)).map((row) => row.title);
}

/* ------------------------------------------------------------------ 典型问题 */

export interface FloodInformationNeed {
  readonly name: string;
  /** 需要获取的内容。 */
  readonly what: string;
  /** 通常从哪里获得。 */
  readonly acquisition: string;
  /** 这一项对应的依据卡。 */
  readonly evidenceId: string;
}

export interface FloodIssue {
  readonly id: string;
  readonly key: string;
  readonly title: string;
  readonly context: string;
  readonly summary: string;
  readonly why: string;
  readonly questions: readonly string[];
  readonly information: readonly FloodInformationNeed[];
  readonly capabilityIds: readonly string[];
  readonly evidenceIds: readonly string[];
  readonly caseIds: readonly string[];
  /** 当前问题在概述区使用的示意图。 */
  readonly image: string;
}

export const floodIssues: readonly FloodIssue[] = [
  {
    id: 'FL-01',
    key: 'rain',
    title: '暴雨雨情与积水位置核实',
    context: '城市道路、低洼片区、雨量／水位监测点与多源报告',
    summary: '降雨和积水信息可能来自气象、水务、视频与群众报告，不同时间和地点不能混成同一结论。',
    why: '同一条“积水”线索可能混合了不同时间、不同地点和不同观测方式；未核实前不能把它当作同一事实。',
    questions: [
      '降雨预警由哪个机构在什么时间发布？',
      '积水位置、观测时间和观测方式是否可追溯？',
      '多源线索中哪一条已经由授权部门核实？',
    ],
    information: [
      {
        name: '降雨预警与发布时间',
        what: '预警级别、发布机构、发布时间、适用区域与有效期。',
        acquisition: '气象部门正式预警、应急管理部门转发记录、现场报告（需注明来源与时间）',
        evidenceId: 'E01',
      },
      {
        name: '积水位置与变化时间',
        what: '具体路段或片区、观测时间、观测方式和可复核的深度记录。',
        acquisition: '排水或水务部门观测、视频画面、现场人员报告（深度需注明测量方式）',
        evidenceId: 'E02',
      },
      {
        name: '监测点与采集方式',
        what: '雨量／水位监测点位置、设备类型、采集频率、校准与维护记录。',
        acquisition: '监测设施台账、设备维护记录、数据发布单位说明',
        evidenceId: 'E09',
      },
      {
        name: '低洼点与重要设施',
        what: '周边低洼路段、地下入口、医院、学校、变电站等重要对象的位置。',
        acquisition: '城市排水防涝重点部位台账、道路与设施管理资料',
        evidenceId: 'E03',
      },
      {
        name: '多源线索的核实状态',
        what: '每条线索的来源、时间、核实人和核实状态，以及来源之间的冲突点。',
        acquisition: '多部门联合核实记录、指挥体系通报、原始报告',
        evidenceId: 'E09',
      },
    ],
    capabilityIds: ['K01', 'K02', 'K03'],
    evidenceIds: ['E01', 'E02', 'E03', 'E09'],
    caseIds: [],
    image: '01-street-monitoring.webp',
  },
  {
    id: 'FL-02',
    key: 'river',
    title: '河道洪水与外水影响',
    context: '河道、堤岸、泵站、闸门、排水口与上游来水',
    summary:
      '河流水位上涨、堤岸险情或外水顶托可能与城市排水叠加。不能把河道洪水直接等同于道路积水。',
    why: '河道洪水、水库险情和城市道路积水由不同管理对象负责；混在一起会掩盖真正的来源与影响范围。',
    questions: [
      '河道水位与上游来水信息由哪个部门发布？',
      '堤岸、泵站、闸门和排水口的位置与状态是否可核对？',
      '哪些低洼片区和重要对象位于可能受影响范围？',
    ],
    information: [
      {
        name: '河道水位与水文站记录',
        what: '水文站位置、水位时间序列、观测时间和数据发布机构。',
        acquisition: '水利部门水文站公开信息、属地防汛部门记录',
        evidenceId: 'E04',
      },
      {
        name: '堤岸、泵站、闸门与排水口',
        what: '河堤、泵站、闸门和排水口的位置、管理单位与设计资料。',
        acquisition: '水利工程台账、排水设施资料、属地管理单位',
        evidenceId: 'E04',
      },
      {
        name: '上游来水与流域降水',
        what: '流域降水、上游水库与河道来水的公开信息及发布时间。',
        acquisition: '水利部门公开信息、属地防汛通报、官方媒体',
        evidenceId: 'E18',
      },
      {
        name: '相邻低洼片区与保护对象',
        what: '可能受外水影响的低洼片区、重要设施和人员密集场所。',
        acquisition: '城市防涝重点部位台账、属地管理资料',
        evidenceId: 'E06',
      },
      {
        name: '管理部门的调度与风险信息',
        what: '水利、应急部门已发布的调度安排、风险提示及其适用河流与片区。',
        acquisition: '属地防汛抗旱指挥机构、水利部门正式发布',
        evidenceId: 'E18',
      },
    ],
    capabilityIds: ['K01', 'K04', 'K02'],
    evidenceIds: ['E04', 'E05', 'E06', 'E18'],
    caseIds: ['C03'],
    image: '02-river-gauge.webp',
  },
  {
    id: 'FL-03',
    key: 'underground',
    title: '地铁隧道与地下空间倒灌',
    context: '地铁车站与区间、道路隧道、地下车库、地下商场及连接通道',
    summary: '地下空间入口和连接通道可能成为进水路径，应区分入口积水、内部进水与人员实际被困。',
    why: '入口有积水不等于内部已经进水，内部进水也不等于人员受困；三种状态需要不同来源分别核实。',
    questions: [
      '进水可能从哪些入口或连接通道进入？',
      '内部水位、报警和设施状态由哪个系统记录？',
      '运营管理单位已发布哪些停运、封闭或疏散信息？',
    ],
    information: [
      {
        name: '地下空间入口与连接通道',
        what: '地铁、隧道、地下车库与地下商场的入口、坡道、挡水设施和连接通道位置。',
        acquisition: '建筑与设施图纸、运营单位资料、现场管理记录',
        evidenceId: 'E07',
      },
      {
        name: '排水、挡水、供电与通信设施',
        what: '排水泵、挡水板、供电、照明和通信设施的位置与状态记录。',
        acquisition: '设施台账、维护日志、运营或物业系统',
        evidenceId: 'E11',
      },
      {
        name: '内部水位、监控与报警',
        what: '内部水位观测、监控画面、报警记录和门禁状态。',
        acquisition: '运营监控系统、报警日志、现场核查',
        evidenceId: 'E08',
      },
      {
        name: '受影响人员与运营报告',
        what: '受影响人员数量、位置、联系情况以及运营管理单位的报告。',
        acquisition: '运营单位报告、现场救援记录、医疗交接',
        evidenceId: 'E10',
      },
      {
        name: '官方停运与封闭信息',
        what: '已发布的停运、封闭、清客或交通管制信息及其发布时间与范围。',
        acquisition: '运营单位、交通管理部门正式发布',
        evidenceId: 'E10',
      },
    ],
    capabilityIds: ['K03', 'K05', 'K09'],
    evidenceIds: ['E07', 'E08', 'E10', 'E11'],
    caseIds: ['C01', 'C02'],
    image: '03-underground-inundation.webp',
  },
  {
    id: 'FL-04',
    key: 'drainage',
    title: '排水设施与低洼点状态',
    context: '城市排水管渠、泵站、雨水口与历史易涝点',
    summary:
      '城市排水管渠、泵站和易涝点的状态决定积水信息解释的基础，但不能从一张监控画面推断设施失效。',
    why: '排水设施的设计能力、运行状态和现场积水是三类不同信息；需要分别记录后再判断它们之间的关系。',
    questions: [
      '排水管渠和泵站的设计与布局资料是否可查？',
      '泵站当前运行状态和异常报警来自哪个系统？',
      '易涝点历史记录与现场画面是否指向同一位置？',
    ],
    information: [
      {
        name: '排水管渠与泵站设计资料',
        what: '管渠走向、设计标准、泵站布局和排水口位置。',
        acquisition: '排水管理部门设施资料、设计文件',
        evidenceId: 'E14',
      },
      {
        name: '泵站运行状态与报警',
        what: '泵站启停状态、运行告警、维护与故障记录。',
        acquisition: '泵站运行系统、设施管理单位记录',
        evidenceId: 'E12',
      },
      {
        name: '易涝点历史与实时视频',
        what: '历史积水记录、位置、发生时间和当前视频画面。',
        acquisition: '排水部门易涝点台账、已获准的监控画面',
        evidenceId: 'E03',
      },
      {
        name: '雨水口与低洼区情况',
        what: '道路雨水口位置、堵塞或溢流线索、低洼区积水观察。',
        acquisition: '现场巡查记录、视频画面、群众报告（需核实）',
        evidenceId: 'E02',
      },
      {
        name: '排水管理部门的检查记录',
        what: '汛前检查、设施维护和重点部位巡查记录。',
        acquisition: '排水管理部门正式记录、属地防涝通报',
        evidenceId: 'E12',
      },
    ],
    capabilityIds: ['K06', 'K01', 'K03'],
    evidenceIds: ['E02', 'E03', 'E12', 'E14'],
    caseIds: [],
    image: '01-street-monitoring.webp',
  },
  {
    id: 'FL-05',
    key: 'people',
    title: '人员受困与水域救援需求',
    context: '积水道路、地下空间、被困车辆与水域救援协同',
    summary:
      '雨洪情况下人员位置、受困状态、交通可达性与医疗需求需分别确认；救援资源是否可用不能仅凭装备类型推断。',
    why: '受困、失联和已转移是不同状态；人员线索必须带来源与最后联系时间，不能由相似案例推断。',
    questions: [
      '哪些人员是已确认受困，哪些只是失联报告？',
      '最后联系时间和报告来源是什么？',
      '水流、障碍物和道路条件是否已有专业观察？',
    ],
    information: [
      {
        name: '已确认受困人数与位置',
        what: '已确认受困人员数量、所在位置、状态与确认来源。',
        acquisition: '现场救援记录、运营或属地管理单位报告、医疗交接',
        evidenceId: 'E10',
      },
      {
        name: '人员报告来源与最后联系时间',
        what: '每条人员线索的报告人、报告时间、最后联系方式和核实状态。',
        acquisition: '求助记录、通信记录、多部门联合核实',
        evidenceId: 'E15',
      },
      {
        name: '水流、障碍物与道路观测',
        what: '水流方向和速度的可复核观察、障碍物、道路通行与天气条件。',
        acquisition: '专业现场观察、气象与道路管理部门信息',
        evidenceId: 'E08',
      },
      {
        name: '救援队伍与船艇能力记录',
        what: '水域救援队伍、船艇和专业装备的能力记录（仅在真实接入时显示）。',
        acquisition: '救援队伍正式台账、属地指挥机构',
        evidenceId: 'E15',
      },
      {
        name: '医疗、安置与转运信息',
        what: '伤员救治、人员安置和转运交接的状态与去向。',
        acquisition: '卫生健康部门、属地安置点、医疗转运记录',
        evidenceId: 'E10',
      },
    ],
    capabilityIds: ['K07', 'K08', 'K02'],
    evidenceIds: ['E08', 'E09', 'E10', 'E15'],
    caseIds: ['C01', 'C02'],
    image: '04-command-coordination.webp',
  },
  {
    id: 'FL-06',
    key: 'traffic',
    title: '交通阻断与跨部门信息协同',
    context: '封闭道路、隧道、交通运营与多部门信息协同',
    summary: '积水可能造成道路、地下通道与交通运营受限，需要把管理单位的正式通报与群众线索分开。',
    why: '同一路段可能同时存在积水、封闭、拥堵和替代路线信息；来源与发布时间不一致时，不能合并成一个结论。',
    questions: [
      '哪些封闭和运营调整是管理单位正式发布的？',
      '替代通道信息来自哪里、是否仍然有效？',
      '同一状态是否存在不同部门的不同版本？',
    ],
    information: [
      {
        name: '封闭道路、隧道与运营通报',
        what: '封闭路段、隧道、交通运营调整的内容、发布单位与生效时间。',
        acquisition: '交通管理部门、运营单位正式通告',
        evidenceId: 'E13',
      },
      {
        name: '救援路线与替代通道',
        what: '可用的救援路线、替代通道及其通行条件与来源。',
        acquisition: '交通管理部门发布、道路管理单位资料',
        evidenceId: 'E03',
      },
      {
        name: '交通、水务、消防、医疗报告',
        what: '各部门报告的内容、时间、责任单位和交接状态。',
        acquisition: '部门原始通报、现场指挥记录',
        evidenceId: 'E16',
      },
      {
        name: '事件时间线与来源冲突',
        what: '告警、通报、封闭、救援和恢复的时间顺序及来源冲突。',
        acquisition: '多部门记录、系统日志、官方发布',
        evidenceId: 'E17',
      },
      {
        name: '区域重要设施与受影响人群',
        what: '受影响区域的重要设施、人员密集场所和已确认受影响人群。',
        acquisition: '属地管理资料、运营单位报告、现场核实',
        evidenceId: 'E08',
      },
    ],
    capabilityIds: ['K09', 'K02', 'K04'],
    evidenceIds: ['E03', 'E08', 'E10', 'E13', 'E16', 'E17'],
    caseIds: ['C01', 'C02'],
    image: '04-command-coordination.webp',
  },
] as const;

export function findFloodIssue(key: string): FloodIssue | undefined {
  return floodIssues.find((row) => row.key === key || row.id === key);
}

export const defaultFloodIssue = 'rain';

/** 依据卡在“依据与案例”页里的分组说明。 */
export const floodEvidenceGroupNote: Readonly<Record<FloodEvidenceCategory, string>> = {
  法律法规: '全国人大常委会通过的法律文本位置；规定防洪职责与制度，不构成本地调度指令。',
  政策文件: '国务院及部门政策、通知的公开章节；说明治理与信息发布要求，不证明某地当前设施能力。',
  标准信息: '标准信息页只说明标准名称、编号与适用范围；未取得正文的，不推断具体条款。',
  灾害调查报告:
    '官方灾害调查报告及答记者问；三个案例同属一份调查报告的相关子事件，不拆成多份报告。',
};
